import { Box, Button, Paper, Tooltip, Chip, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { tokens } from '../../theme';
import StyleIcon from '@mui/icons-material/Style';
import InventoryIcon from '@mui/icons-material/Inventory';
import TerrainIcon from '@mui/icons-material/Terrain';
import RentalIcon from '@mui/icons-material/AutoAwesome';
import { DiamondOutlined } from '@mui/icons-material';
import GavelIcon from '@mui/icons-material/Gavel';
import CollectionsIcon from '@mui/icons-material/Collections';

const MarketTabs = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  // Add local state to force re-render when route changes
  const [currentPath, setCurrentPath] = useState(location.pathname);
  
  // Update current path when location changes
  useEffect(() => {
    setCurrentPath(location.pathname);
  }, [location.pathname, location.key]); // Add location.key to force update on navigation
  
  // Market tab configuration - organized in two rows
  const marketTabsConfig = {
    trading: [
      {
        id: 'cards',
        label: 'Cards',
        icon: <StyleIcon />,
        enabled: true,
        description: 'Trade Splinterlands cards',
        route: '/market'
      },
      {
        id: 'items',
        label: 'Items',
        icon: <InventoryIcon />,
        enabled: true,
        description: 'Trade packs, land, totems, and other items',
        route: '/market/items',
      },
      {
        id: 'land',
        label: 'Land',
        icon: <TerrainIcon />,
        enabled: true,
        description: 'Trade land plots and regions',
        route: '/market/land'
      },
    ],
    collection: [
      {
        id: 'mycards',
        label: 'My Cards',
        icon: <CollectionsIcon />,
        enabled: true,
        description: 'View your card collection',
        route: '/market/mycards'
      },
      {
        id: 'bids',
        label: 'My Bids',
        icon: <GavelIcon />,
        enabled: true,
        description: 'View and manage all your bids',
        route: '/market/bids'
      },
    ]
  };

  // Determine active tab based on current route
  const getActiveTab = () => {
    if (currentPath === '/market') return 'cards';
    if (currentPath === '/market/mycards') return 'mycards';
    if (currentPath === '/market/myitems') return 'myitems';
    if (currentPath === '/market/myland') return 'myland';
    if (currentPath === '/market/land') return 'land';
    if (currentPath === '/market/items') return 'items';
    if (currentPath === '/market/bids') return 'bids';
    if (currentPath === '/market/rentals') return 'rentals';
    if (currentPath === '/market/sps') return 'sps';
    return 'cards';
  };

  const activeTab = getActiveTab();

  // Helper function to render a row of tabs
  const renderTabRow = (tabs) => (
    <Box 
      display="flex" 
      justifyContent="flex-start"
      sx={{
        flexDirection: 'row',
        gap: 0.5,
        overflowX: 'auto',
        '&::-webkit-scrollbar': { height: '3px' },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: colors.primary[300],
          borderRadius: '4px'
        }
      }}
    >
      {tabs.map((tab) => (
        <Tooltip 
          key={tab.id} 
          title={tab.description} 
          arrow 
          placement="bottom"
        >
          <Button
            variant="text"
            disabled={!tab.enabled}
            onClick={() => {
              if (tab.enabled) {
                navigate(tab.route);
              }
            }}
            sx={{
              px: 1.5,
              py: 0.5,
              minWidth: 'auto',
              fontSize: '0.8rem',
              backgroundColor: activeTab === tab.id && tab.enabled 
                ? colors.greenAccent[600] 
                : 'transparent',
              color: activeTab === tab.id && tab.enabled 
                ? colors.grey[100] 
                : tab.enabled 
                  ? colors.grey[300] 
                  : colors.grey[500],
              fontWeight: activeTab === tab.id && tab.enabled ? '600' : 'normal',
              borderRadius: 1,
              '&:hover': {
                backgroundColor: tab.enabled 
                  ? activeTab === tab.id 
                    ? colors.greenAccent[700] 
                    : colors.primary[500]
                  : 'transparent',
                cursor: tab.enabled ? 'pointer' : 'not-allowed'
              },
              '&:disabled': {
                color: colors.grey[500]
              },
              '& .MuiButton-startIcon': {
                margin: 0,
                mr: 0.5
              }
            }}
            startIcon={tab.icon}
          >
            {tab.label}
          </Button>
        </Tooltip>
      ))}
    </Box>
  );


  return (
    <Box key={currentPath}>
      <Box display="flex" gap={1.5} flexWrap="wrap">
        {/* Trading tabs */}
        {renderTabRow(marketTabsConfig.trading)}
        
        {/* Divider */}
        <Box sx={{ width: '1px', backgroundColor: colors.primary[300], mx: 0.5 }} />
        
        {/* Collection tabs */}
        {renderTabRow(marketTabsConfig.collection)}
      </Box>
    </Box>
  );
};

export default MarketTabs;
